import React, { useState, useReducer, useEffect, useRef } from "react";
import { useSWRConfig } from "swr";
import { useDispatch } from "react-redux";
import {
  GridComponent,
  Page,
  Inject,
  ColumnDirective,
  ColumnsDirective,
  Filter,
  Sort
} from "@syncfusion/ej2-react-grids";

// Components
import Spinner from "../Components/Spinner";
import DeleteModal from "../Components/Modals/Delete-modal";
import ToastError from "../Components/ToastError";
import { toDateISOString } from "../../Services/string.service";

// Services
import { manufacturerService } from "../../Services/manufacturer.service";
import { paginationService } from "../../Services/pagination.service";

// Icons
import { SiAddthis } from "react-icons/si";
import { AiFillEdit, AiFillDelete } from "react-icons/ai";

// Constants
const _pageTitle = "Cadastro de Montadoras";
const _listTitle = "Lista de Montadoras";
const _registerTitle = "Dado da Montadora";

const formInitialValues = {
  id: 0,
  name: "",
};

const formReducer = (state, event) => {
  if (event.reset) {
    return formInitialValues;
  }
  return {
    ...state,
    [event.name]: event.value,
  };
};

export default function Manufacturers() {
  const [formData, setFormData] = useReducer(formReducer, {});
  const [isLoading, setIsLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [isDeleting, setIsDeleting] = useState({ show: false, itemName: "" });
  const [editingItem, setEditingItem] = useState({});
  const [showToast, setShowToast] = useState(false);
  const [manufacturersData, setManufacturersData] = useState([]);
  const [pageSize, setPageSize] = useState(15);

  const gridAnchorRef = useRef(null);
  const ROW_HEIGHT = 30;
  const HEADER_PAGER_BUDGET = 100;
  const BOTTOM_MARGIN = 30;
  const MIN_PAGE = 5;
  const MAX_PAGE = 100;

  const dispatch = useDispatch();
  dispatch({ type: "title", value: _pageTitle });

  const { mutate } = useSWRConfig();

  const handleCloseModal = () => {
    setIsDeleting({ show: false, itemName: "" });
  };

  const handleChange = (event) => {
    setFormData({
      name: event.target.name,
      value: event.target.value,
    });
  };



  const onEdit = (item) => {
    setEditingItem(item);
    setFormData({
      name: "id",
      value: item.automakerId,
    });

    setFormData({
      name: "name",
      value: item.description,
    });

    setShowForm(true);
  };

  const onNew = () => {
    setFormData({
      reset: true,
    });
    setShowForm(true);
    setEditingItem({});
  };

  const onCancel = () => {
    setFormData({
      reset: true,
    });
    setEditingItem({});
    setShowForm(false);
  };

  const onDelete = (item) => {
    setIsDeleting({ show: true, itemName: item.description, itemRemoveId: item.automakerId });
    setEditingItem(item);
  };

  const handleRemove = async (id) => {
    if (id) {
      try {
        setIsLoading(true);
        const updatedItem = { ...editingItem };
        updatedItem.active = "N";
        await manufacturerService.update(updatedItem);
        const updatedManufacturers = await manufacturerService.list();
        setManufacturersData((updatedManufacturers || []).filter(manufacturer => manufacturer.active === "S"));
      } catch (error) {
        console.log(error);
        setShowToast(true);
      } finally {
        setTimeout(() => {
          setIsLoading(false);
          setShowForm(false);
          setFormData({
            reset: true,
          });
          setEditingItem({});
        }, 1000);
      }
    }
  };

  const onCloseToast = () => {
    setShowToast(false);
  };

  const onSave = async () => {
    if (formData.name) {
      setIsLoading(true);

      const payload = {
        description: formData.name,
        infoCar: "N",
        active: "S",
        created: toDateISOString(new Date()),
      };

      try {
        setIsLoading(true);

        if (formData.id) payload["id"] = Number(formData.id || 0);

        let response = "";
        if (payload.id > 0)
          response = await manufacturerService.update(payload);
        else response = await manufacturerService.create(payload);

        if (response == null) setShowToast(true);
        if (response) {
          const updatedManufacturers = await manufacturerService.list();
          setManufacturersData((updatedManufacturers || []).filter(manufacturer => manufacturer.active === "S"));
        }
      } catch (error) {
        console.log(error);
        setShowToast(true);
      } finally {
        setTimeout(() => {
          setIsLoading(false);
          setShowForm(false);
          setFormData({
            reset: true,
          });
          setEditingItem({});
        }, 1000);
      }
    }
  };

  useEffect(() => {
    (async () => {
      setIsLoading(true);
      try {
        const manufacturers = await manufacturerService.list();
        setManufacturersData((manufacturers || []).filter(manufacturer => manufacturer.active === "S"));
      } catch (error) {
        console.error(error);
      } finally {
        setIsLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (!isLoading && manufacturersData.length > 0) {
      paginationService.calculateGridMetrics(
        gridAnchorRef,
        BOTTOM_MARGIN,
        HEADER_PAGER_BUDGET,
        ROW_HEIGHT,
        MIN_PAGE,
        MAX_PAGE,
        manufacturersData,
        window.innerHeight,
        setPageSize
      );
    }

    let t = null;
    const onResize = () => {
      if (t) window.clearTimeout(t);
      t = window.setTimeout(() => paginationService.calculateGridMetrics(
        gridAnchorRef,
        BOTTOM_MARGIN,
        HEADER_PAGER_BUDGET,
        ROW_HEIGHT,
        MIN_PAGE,
        MAX_PAGE,
        manufacturersData,
        window.innerHeight,
        setPageSize
      ), 50);
    };

    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
      if (t) window.clearTimeout(t);
    };
  }, [isLoading, manufacturersData]);

  return (
    <>
      <div className='container-xxl flex-grow-1 container-p-y'>
        {isLoading ? <Spinner /> : ""}

        <ToastError show={showToast} closeToast={onCloseToast} />

        <DeleteModal
          itemName={isDeleting.itemName}
          itemRemoveId={isDeleting.itemRemoveId}
          callBackRemove={handleRemove}
          show={isDeleting.show}
          handleClose={handleCloseModal}
        />

        <div
          className='card'
          style={showForm || isLoading ? { display: "none" } : { display: "" }}>
          <div className='d-flex justify-content-between'>
            <div className='p-2 card-header'>
              <h5>{_listTitle}</h5>
            </div>
            <div className='p-2'></div>
            <div className='p-2'>
              <SiAddthis className='icon-new' onClick={onNew} />
            </div>
          </div>

          <div ref={gridAnchorRef} />
          <GridComponent
            dataSource={manufacturersData}
            allowPaging
            allowSorting={true}
            allowFiltering={true}
            filterSettings={{ type: "Menu" }}
            pageSettings={{ pageSize }}
            rowHeight={ROW_HEIGHT}
            height={"auto"}
            style={{ marginTop: 16 }}
            locale="pt-BR"
            actionComplete={() => {
              setTimeout(() => paginationService.calculateGridMetrics(
                gridAnchorRef,
                BOTTOM_MARGIN,
                HEADER_PAGER_BUDGET,
                ROW_HEIGHT,
                MIN_PAGE,
                MAX_PAGE,
                manufacturersData,
                window.innerHeight,
                setPageSize
              ), 50);
            }}
          >
            <ColumnsDirective>
              <ColumnDirective field="automakerId" headerText="Id" width="80" />
              <ColumnDirective field="description" headerText="Descrição" width="200" />
              <ColumnDirective field="active" headerText="Ativo" width="80" />
              <ColumnDirective headerText="Ações" width="60" template={(props) => (
                <div style={{ display: 'flex', gap: '8px' }}>
                  <AiFillEdit 
                    style={{ cursor: 'pointer', color: 'var(--text-primary)', fontSize: '16px' }}
                    onClick={(e) => {
                      e.stopPropagation();
                      onEdit(props);
                    }}
                    title="Editar"
                  />
                  <AiFillDelete 
                    style={{ cursor: 'pointer', color: 'var(--text-primary)', fontSize: '16px' }}
                    onClick={(e) => {
                      e.stopPropagation();
                      onDelete(props);
                    }}
                    title="Deletar"
                  />
                </div>
              )} />
            </ColumnsDirective>
            <Inject services={[Page, Filter, Sort]} />
          </GridComponent>
        </div>

        <div
          className='row py-3'
          style={
            !showForm || isLoading ? { display: "none" } : { display: "block" }
          }>
          <div className='col-6'>
            <div className='card'>
              <h5 className='card-header'>{_registerTitle}</h5>
              <div className='card-header'>
                {formData.id > 0 ? (
                  <div className='row mb-3'>
                    <label
                      className='col-sm-2 col-form-label'
                      htmlFor='basic-default-name'>
                      ID
                    </label>
                    <div className='col-sm-10'>
                      <label
                        className='col-sm-2 col-form-label'
                        htmlFor='basic-default-name'>
                        {formData.id}
                      </label>
                    </div>
                  </div>
                ) : (
                  ""
                )}

                <input
                  type='hidden'
                  value={formData.infoCar || ""}
                  id='infoCar'
                  name='infoCar'
                />

                <div className='row mb-3'>
                  <label
                    className='col-sm-2 col-form-label'
                    htmlFor='basic-default-name'>
                    Nome
                  </label>
                  <div className='col-sm-10'>
                    <input
                      type='text'
                      className='form-control'
                      name='name'
                      placeholder='Nome'
                      value={formData.name || ""}
                      onChange={handleChange}
                    />
                  </div>
                </div>

                <div
                  className='row justify-content-end'
                  style={{ textAlign: "end" }}>
                  <div className='col-ls-8 col-md-8 col-xs-12 mb-3'>
                    <button
                      type='button'
                      className='btn btn-warning'
                      style={{ marginRight: "10px" }}
                      onClick={onCancel}>
                      Cancelar
                    </button>

                    <button
                      type='button'
                      className='btn btn-primary'
                      onClick={onSave}
                      disabled={isLoading}>
                      Salvar
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}